import { useGLTF } from "@react-three/drei";
import { useMemo, useEffect } from "react";
import * as THREE from "three";
import GlbLoader from "../../Utils/GlbLoader";
import { assignMaterialToObjects } from "../../Utils/AssignMaterialToObject";
import { stateMemory, stateContentReady } from "../../store";
import { useSnapshot } from "valtio";

export default function KlimaRoomShell({ visible }) {
  const { ParkettColor, ParkettNormal, ParkettRough, imperfectionRoughMap } = GlbLoader();
  const snapStateMemory = useSnapshot(stateMemory);
  const contentReady = useSnapshot(stateContentReady)[2];
  const gltf = useGLTF("../models/Museum01/Klima.glb");

  const mats = useMemo(() => {
    ParkettColor.repeat.set(15, 15);
    ParkettNormal.repeat.set(15, 15);
    ParkettRough.repeat.set(15, 15);

    const m = {
      blackMattFloorMaterial: new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#262626", roughnessMap: imperfectionRoughMap }),
      whiteGlossyWall:        new THREE.MeshStandardMaterial({ roughness: 0.05, metalness: 0, color: "#D6D5D5" }),
      parkettMaterial:        new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0, map: ParkettColor, roughnessMap: ParkettRough, normalMap: ParkettNormal }),
    };

    assignMaterialToObjects(gltf.scene, ["Podest", "FloorBlack"], m.blackMattFloorMaterial);
    assignMaterialToObjects(gltf.scene, ["Trennwaende", "Walls"], m.whiteGlossyWall);
    assignMaterialToObjects(gltf.scene, ["Plane023"], m.parkettMaterial);

    return m;
  }, [gltf.scene]);

  useEffect(() => {
    if (!visible || !contentReady) return;

    const base = snapStateMemory.memoryLowDevice
      ? "../overallTextures/KlimaMainPoint/Dachstein/LOW/"
      : "../overallTextures/KlimaMainPoint/Dachstein/";

    let active = true;
    const loadedTextures = [];

    const applyAO = (tex, ...targets) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.channel = 1;
      loadedTextures.push(tex);
      targets.forEach(m => { m.aoMap = tex; m.aoMapIntensity = 0.8; m.needsUpdate = true; });
    };

    const loader = new THREE.TextureLoader();
    loader.load(base + "BakeKlimaFloorAO.webp", (tex) => applyAO(tex, mats.blackMattFloorMaterial, mats.parkettMaterial));
    loader.load(base + "BakeKlimaWallsAO.webp", (tex) => applyAO(tex, mats.whiteGlossyWall));

    return () => {
      active = false;
      loadedTextures.forEach(tex => tex.dispose());
      [mats.blackMattFloorMaterial, mats.parkettMaterial, mats.whiteGlossyWall].forEach(m => {
        m.aoMap = null; m.needsUpdate = true;
      });
    };
  }, [visible, contentReady]);

  return <primitive object={gltf.scene} visible={visible} />;
}
